import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Search, StickyNote, Trash2, User } from 'lucide-react';
import { SLOT_LABEL, STATUSES, STATUS_DELETABLE, STATUS_LABEL, formatDay, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import { useAutoRefresh } from '../../useAutoRefresh.js';
import { BookingsHead, LEAVE_LABEL, addDays, useBookingEditor, useBookingRefs } from './shared.jsx';
import { problem } from './util.js';
import { ConfirmDelete } from './DeleteBookings.jsx';

// Bookings list: a row per booking, for finding and checking. Search looks
// across all dates. The Team column names the pair on that team on the
// booking's date. Daily or Monthly steps through the dates; Unassigned lists
// every booking not on a team yet, dated or not. Office only, online only.
// Full admins also get a Delete button on bookings nobody has been out to.

const MODES = [
  { id: 'day', label: 'Daily' },
  { id: 'month', label: 'Monthly' },
  { id: 'unassigned', label: 'Unassigned' },
];
const MODE_KEY = 'nu.bookingsListMode';
const savedMode = () => { // an old saved 'undated' falls back to Daily
  try { return MODES.some((m) => m.id === localStorage.getItem(MODE_KEY)) ? localStorage.getItem(MODE_KEY) : 'day'; } catch { return 'day'; }
};
const monthOf = (day) => `${day.slice(0, 7)}-01`;
function shiftMonth(monthStart, n) {
  const [y, m] = monthStart.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 10);
}
const monthEnd = (monthStart) => addDays(shiftMonth(monthStart, 1), -1);
const monthLabel = (monthStart) => new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${monthStart}T12:00:00Z`));
const dayLabel = (day) => new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`));

export default function BookingsList({ me }) {
  const canEdit = me.capabilities.includes('page.bookings-list.edit');
  const canDelete = me.capabilities.includes('platform.admin');
  const [deleting, setDeleting] = useState(null);
  const today = todayUk();
  const { types, teams, colours } = useBookingRefs();
  const [mode, setModeState] = useState(savedMode);
  const [day, setDay] = useState(today);
  const [monthStart, setMonthStart] = useState(() => monthOf(today));
  const setMode = (m) => {
    setModeState(m);
    try { localStorage.setItem(MODE_KEY, m); } catch { /* private mode: lasts this visit */ }
  };
  const [teamId, setTeamId] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // A search looks everywhere, not only in the chosen dates.
  const [from, to] = useMemo(() => {
    if (debouncedQ || mode === 'unassigned') return [null, null];
    if (mode === 'day') return [day, day];
    return [monthStart, monthEnd(monthStart)];
  }, [mode, day, monthStart, debouncedQ]);

  const load = useCallback(async () => {
    setError(null);
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (mode === 'unassigned' && !debouncedQ) p.set('teamId', 'none');
    else if (teamId) p.set('teamId', teamId);
    if (status) p.set('status', status);
    if (debouncedQ) p.set('q', debouncedQ);
    else if (mode !== 'unassigned') p.set('dated', 'yes');
    try {
      setRows((await api(`/bookings?${p}`)).bookings);
    } catch (e) {
      setError(problem(e));
    }
  }, [from, to, teamId, status, debouncedQ, mode]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  const { notice, setNotice, openBooking, openNew, modal } = useBookingEditor({ canEdit, types, teams, reload: load });
  // ?booking=123 opens that booking (links in alert emails).
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const id = Number(params.get('booking'));
    if (Number.isInteger(id) && id > 0) {
      openBooking(id);
      setParams((p) => { p.delete('booking'); return p; }, { replace: true });
    }
  }, [params, setParams, openBooking]);

  const counts = useMemo(() => {
    const c = {};
    for (const b of rows || []) c[b.status] = (c[b.status] || 0) + 1;
    return c;
  }, [rows]);

  return (
    <div className="stack">
      <BookingsHead
        title="Bookings list"
        intro="Every booking, to find and check. Click a booking to see or change it. Plan the week on the Bookings calendar."
        canEdit={canEdit}
        onNew={openNew}
        onImported={load}
        notice={notice}
      />

      <section className="card">
        <div className="filters">
          <div className="view-toggle" role="group" aria-label="Dates">
            {MODES.map((m) => (
              <button key={m.id} type="button" className={`btn${mode === m.id ? ' active' : ''}`} aria-pressed={mode === m.id} disabled={Boolean(debouncedQ)} onClick={() => setMode(m.id)}>{m.label}</button>
            ))}
          </div>
          {!debouncedQ && mode !== 'unassigned' && (
            <div className="week-nav" role="group" aria-label={mode === 'day' ? 'Day' : 'Month'}>
              <button type="button" className="btn icon-only" aria-label={mode === 'day' ? 'Previous day' : 'Previous month'}
                onClick={() => (mode === 'day' ? setDay((d) => addDays(d, -1)) : setMonthStart((m) => shiftMonth(m, -1)))}><ChevronLeft size={18} aria-hidden="true" /></button>
              <button type="button" className="btn" onClick={() => (mode === 'day' ? setDay(today) : setMonthStart(monthOf(today)))}>{mode === 'day' ? 'Today' : 'This month'}</button>
              <button type="button" className="btn icon-only" aria-label={mode === 'day' ? 'Next day' : 'Next month'}
                onClick={() => (mode === 'day' ? setDay((d) => addDays(d, 1)) : setMonthStart((m) => shiftMonth(m, 1)))}><ChevronRight size={18} aria-hidden="true" /></button>
              {mode === 'day' && <input type="date" className="filter-date" value={day} aria-label="Day" onChange={(e) => e.target.value && setDay(e.target.value)} />}
              <span className="week-label">{mode === 'day' ? `${dayLabel(day)} ${formatDay(day)}` : monthLabel(monthStart)}</span>
            </div>
          )}
          <label className="search">
            <Search size={18} aria-hidden="true" />
            <input type="search" placeholder="Search NU reference, postcode, address, customer or client ref" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <select value={mode === 'unassigned' && !debouncedQ ? 'none' : teamId} disabled={mode === 'unassigned' && !debouncedQ} onChange={(e) => setTeamId(e.target.value)} aria-label="Team">
            <option value="">All teams</option>
            <option value="none">Not on a team</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}{t.active ? '' : ' (closed)'}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        {debouncedQ && <p className="small filter-line">Searching all dates for “{debouncedQ}”.</p>}
        {rows && rows.length > 0 && (
          <p className="small muted">
            {rows.length} booking{rows.length === 1 ? '' : 's'}
            {Object.keys(counts).length > 1 && ` · ${STATUSES.filter((s) => counts[s.value]).map((s) => `${counts[s.value]} ${s.label.toLowerCase()}`).join(' · ')}`}
          </p>
        )}

        {error && <div className="empty"><p>{error}</p><button type="button" className="btn" onClick={load}>Try again</button></div>}
        {!error && !rows && <p className="muted">Loading…</p>}
        {rows && !rows.length && <p className="muted">No bookings{debouncedQ ? ' match that search' : mode === 'unassigned' ? ' waiting for a team' : mode === 'day' ? ' on this day' : ' this month'}.{canEdit && !debouncedQ ? ' Use New booking.' : ''}</p>}

        {rows?.length > 0 && (
          <div className="table-scroll">
            <table className="table roster clickable">
              <thead>
                <tr><th>Ref</th><th>Date</th><th>Slot</th><th>Team</th><th>Status</th><th>Type</th><th>Customer</th><th>Address</th><th>Postcode</th><th>Client ref</th><th aria-label="Notes" />{canDelete && <th aria-label="Delete" />}</tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={b.id} tabIndex={0} onClick={() => openBooking(b.id)} onKeyDown={(e) => { if (e.key === 'Enter') openBooking(b.id); }}>
                    <td data-label="Ref"><strong>{b.reference}</strong></td>
                    <td data-label="Date">{b.bookingDate ? formatDay(b.bookingDate) : <span className="muted">No date</span>}</td>
                    <td data-label="Slot">{SLOT_LABEL[b.slot] || <span className="muted">–</span>}{b.slot && b.slotAuto && <span className="slot-auto" title="Set by Optimise on Plan the day">auto</span>}</td>
                    <td data-label="Team">
                      {b.teamName
                        ? (
                          <div className="team-cell" style={{ '--team': colours.get(b.teamId) }}>
                            <strong>{b.teamName}</strong>
                            {b.crew?.length > 0 && (
                              <ul className="crew">
                                {b.crew.map((c) => (
                                  <li key={c.id}>
                                    <User size={13} aria-hidden="true" />{c.name}
                                    {c.leave && <span className="tag tag-leave" title="Holiday booked in BrightHR">{LEAVE_LABEL[c.leave]}</span>}
                                  </li>
                                ))}
                              </ul>
                            )}
                            {b.bookingDate && b.crew?.length === 0 && <div className="small muted">Nobody in the team that day</div>}
                          </div>
                        )
                        : <span className="muted">None</span>}
                    </td>
                    <td data-label="Status"><span className={`status status-${b.status}`}>{STATUS_LABEL[b.status]}</span>{b.openDefects > 0 && <span className="defect-badge" title={`${b.openDefects} open defect${b.openDefects === 1 ? '' : 's'}`}>Defect</span>}</td>
                    <td data-label="Type">{b.type.client} · {b.type.workType}</td>
                    <td data-label="Customer">{b.customerName || <span className="muted">–</span>}</td>
                    <td data-label="Address" className="wrap">{[b.addressLine1, b.town].filter(Boolean).join(', ')}</td>
                    <td data-label="Postcode">{b.postcode}</td>
                    <td data-label="Client ref">{b.clientRef || <span className="muted">–</span>}</td>
                    <td data-label="Notes">{b.noteCount > 0 && <span className="note-count" title={`${b.noteCount} note${b.noteCount === 1 ? '' : 's'}`}><StickyNote size={16} aria-hidden="true" /> {b.noteCount}</span>}</td>
                    {canDelete && (
                      <td data-label="Delete">
                        {STATUS_DELETABLE.includes(b.status) && (
                          <button type="button" className="icon-btn row-delete" title={`Delete ${b.reference}`} aria-label={`Delete ${b.reference}`} onClick={(e) => { e.stopPropagation(); setDeleting(b); }} onKeyDown={(e) => e.stopPropagation()}>
                            <Trash2 size={17} aria-hidden="true" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {modal}
      {deleting && (
        <ConfirmDelete
          booking={deleting}
          onClose={() => setDeleting(null)}
          onDone={(ref) => { setDeleting(null); setNotice({ tone: 'ok', text: `${ref} deleted. It's in the audit trail with your reason.` }); load(); }}
        />
      )}
    </div>
  );
}
